import { Component, Input, OnInit, OnDestroy, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SubSink } from 'subsink';
import { Note, NoteResponse, UWBaseProps } from '@Underwriting/models';
import { NotificationService, NotificationSeverity, UWTaskNotesDataService, CaseWorkbenchTaskNoteResponse, IdleService } from '@Services';
import moment from 'moment';
import { AppService } from '@App/app.service';
import { UWNotesFeatureToggleDataService } from '@CaseSummary/underwriting/services';
import { RichTextEditorComponent } from '@Shared/components/rich-text-editor';
import SharedAddNoteFormComponent from '@CaseSummary/underwriting/components/shared-add-note-form/shared-add-note-form.component';

@Component({
    selector: 'uw-task-notes-panel',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        RichTextEditorComponent,
        SharedAddNoteFormComponent
    ],
    providers: [UWTaskNotesDataService],
    templateUrl: './uw-task-notes-panel.component.html',
    styleUrls: ['./uw-task-notes-panel.component.scss']
})
export default class UWTaskNotesPanel implements OnInit, OnDestroy {
    @Input() baseProps: UWBaseProps;
    @Input() taskId: string;
    @Input() caseId?: string;
    @Input() readonly = false;
    @Input() preloadedNotes?: CaseWorkbenchTaskNoteResponse[];

    @ViewChild(SharedAddNoteFormComponent) sharedAddNoteForm: SharedAddNoteFormComponent;
    @ViewChild('notesList') notesList?: ElementRef<HTMLUListElement>;

    notes: CaseWorkbenchTaskNoteResponse[] = [];
    noteForm: UntypedFormGroup;
    noteError = false;
    isLoading = false;
    isEditableNoteFeatureEnabled = false;
    isEditableNoteAccountSettingEnabled = false;
    editableNoteDaysBackAccountSetting: number = 0;
    currentUserId: string = '';
    editingNoteId: string | null = null;
    editedMessage: string = '';
    newMessage: string = '';
    disabled = false;
    updatedRequirementTaskNoteStyling = false;
    expandedNotes = new Set<string>();

    @Output() editingStateChanged = new EventEmitter<boolean>();
    subs = new SubSink();

    constructor(
        private readonly fb: UntypedFormBuilder,
        private readonly taskNotesService: UWTaskNotesDataService,
        private readonly notificationService: NotificationService,
        private readonly uwNotesFeatureToggle: UWNotesFeatureToggleDataService,
        private readonly idleService: IdleService,
        private readonly appService: AppService
    ) {
        this.noteForm = this.fb.group({
            note: ['', Validators.required]
        });
    }

    ngOnInit() {
        if (this.preloadedNotes) {
            this.notes = this.preloadedNotes;
            this.configureNotes();
        } else {
            this.loadNotes();
        }

        if (this.baseProps?.account) {
            this.subs.add(this.uwNotesFeatureToggle.loadHiddenNotesFeatureSetting(this.baseProps.account).subscribe((noteSettings) => {
                this.isEditableNoteFeatureEnabled = noteSettings.editableNoteFeatureToggle;
                this.isEditableNoteAccountSettingEnabled = noteSettings.editableNoteAccountSetting;
                this.editableNoteDaysBackAccountSetting = noteSettings.editableNoteDaysBackAccountSetting;
                this.updatedRequirementTaskNoteStyling = noteSettings.updatedRequirementTaskNoteStyling;
            }));
        }

        
        this.currentUserId = this.appService.getUserId();
    }

    canEditNote(note: CaseWorkbenchTaskNoteResponse): boolean {
        const result = this.isEditableNoteFeatureEnabled
                    && this.isEditableNoteAccountSettingEnabled
                    && moment().utc().diff(moment.utc(note.createdOn), 'days') < this.editableNoteDaysBackAccountSetting
                    && !this.readonly
                    && !this.baseProps?.readonly
                    && note.createdBy === this.currentUserId
                    && this.editingNoteId !== note.id

        return result;
    }

    disableEditNote(note: NoteResponse): boolean {
        return this.editingNoteId !== null && this.editingNoteId !== note.id;
    }

    setEditingState(isEditing: boolean) {
        this.editingStateChanged.emit(isEditing);
    }

    editNote(note: Note) {
        this.editingNoteId = note.id;
        this.editedMessage = note.message;
        this.idleService.areUwwNotesInEditMode = true;
        this.setEditingState(true);
    }

    isNoteExpanded(id: string): boolean {
        return this.expandedNotes.has(id);
    }

    toggleNoteExpanded(id: string): void {
        if (this.expandedNotes.has(id)) {
            this.expandedNotes.delete(id);
        } else {
            this.expandedNotes.add(id);
        }
    }

    updateExpandLinkVisibility(id: string): void {
        const message = document.getElementById(`message-${id}`);
        const expandLink = document.getElementById(`expand-${id}`);
        if (!message || !expandLink) return;

        const lineHeight = Number.parseFloat(getComputedStyle(message).lineHeight) || 20;
        message.classList.remove('message-condensed');
        const fullHeight = message.scrollHeight;
        message.classList.add('message-condensed');

        expandLink.style.display = fullHeight > lineHeight * 3 ? 'block' : 'none';
    }

    displayMessage(message: string): string {
        return message?.replace(/&nbsp;/g, ' ') ?? '';
    }

    loadNotes() {
        this.isLoading = true;
        const effectiveCaseId = this.caseId ?? this.baseProps?.case?.id;
        this.subs.add(
            this.taskNotesService.getTaskNotes(effectiveCaseId, this.taskId).subscribe({
                next: (notes) => {
                    this.notes = notes || [];

                    this.configureNotes();

                    

                    this.isLoading = false;
                },
                error: () => {
                    this.isLoading = false;
                    this.notificationService.showNotification({
                        severity: NotificationSeverity.Error,
                        message: 'Failed to load task notes.',
                        log: true
                    });
                }
            })
        );
    }

    configureNotes() {
        this.notes.forEach(note => {
        note.createdOnDisplay = moment.utc(note.createdOn).tz('America/Chicago').format("M/D/YYYY h:mm a");
            if (note.updatedOn)
            {
                note.updatedOnDisplay = moment.utc(note.updatedOn).tz('America/Chicago').format("M/D/YYYY h:mm a");
            }
        });
        this.notes.sort((a: CaseWorkbenchTaskNoteResponse, b: CaseWorkbenchTaskNoteResponse) => {
            const first = moment(a.createdOn);
            const second = moment(b.createdOn);

            return second.diff(first, 'seconds');
        });

        if (this.updatedRequirementTaskNoteStyling) {
            setTimeout(() => this.notes.forEach(note => this.updateExpandLinkVisibility(note.id)));
        }
    }

    saveNote(_note?: Note){
        this.noteError = false;
        this.subs.add(
            this.taskNotesService.updateTaskNote(this.caseId ?? this.baseProps?.case?.id, this.taskId, { message: this.editedMessage, isInternalNote: false, id: this.editingNoteId }).subscribe({
                next: (notes) => {
                    const editedNoteId = this.editingNoteId;
                    this.cleanupEditor();
                    this.editingNoteId = null;
                    this.idleService.areUwwNotesInEditMode = false;
                    this.notes = notes;

                    this.configureNotes();

                    this.noteForm.reset();
                    this.notificationService.showNotification({
                        severity: NotificationSeverity.Success,
                        message: 'Note updated successfully.'
                    });

                    this.focusEditedNote(editedNoteId);
                },
                error: () => {
                    this.notificationService.showNotification({
                        severity: NotificationSeverity.Error,
                        message: 'Failed to update note. Please try again.',
                        log: true
                    });
                }
            })
        );
    }

    discardEdit() {
        this.cleanupEditor();
        this.editingNoteId = null;
        this.editedMessage = '';
        this.idleService.areUwwNotesInEditMode = false;
    }

    private cleanupEditor() {}

    private focusEditedNote(noteId: string | null) {
        if (!noteId) {
            return;
        }

        setTimeout(() => {
            const element = this.notesList?.nativeElement
                .querySelector<HTMLElement>(`[id="note-${noteId}"]`);
            element?.focus();
        });
    }

    addNote(message?: string) {
        if (!message || message === '<p></p>' || message.trim() === '') {
            this.noteError = true;
            this.notificationService.showNotification({
                severity: NotificationSeverity.Info,
                message: 'Please enter a valid note.'
            });
            return;
        }

        this.noteError = false;
        this.subs.add(
            this.taskNotesService.addTaskNote(this.caseId ?? this.baseProps?.case?.id, this.taskId, { message, isInternalNote: false }).subscribe({
                next: (note) => {
                    this.notes = [...this.notes, note];
                    this.newMessage = '';
                    this.sharedAddNoteForm?.clearForm();
                    
                    this.configureNotes();

                    this.notificationService.showNotification({
                        severity: NotificationSeverity.Success,
                        message: 'Note added successfully.'
                    });
                },
                error: () => {
                    this.notificationService.showNotification({
                        severity: NotificationSeverity.Error,
                        message: 'Failed to add note. Please try again.',
                        log: true
                    });
                }
            })
        );
    }

    clearNote() {
        this.sharedAddNoteForm?.clearForm();
        this.noteError = false;
        this.newMessage = '';
    }

    ngOnDestroy() {
        this.cleanupEditor();
        this.subs.unsubscribe();
    }
}
